import { useState, useCallback, useMemo } from 'react'
import type { GalleryImage, LightboxState } from '../types'

interface UseLightboxOptions {
  images: GalleryImage[]
  loop?: boolean
}

interface UseLightboxReturn {
  state: LightboxState
  open: (index: number) => void
  close: () => void
  closeAtIndex: (index: number) => void
  next: () => void
  prev: () => void
  goTo: (index: number) => void
  currentImage: GalleryImage | null
  totalImages: number
  lastViewedIndex: number
}

export function useLightbox({ images, loop = true }: UseLightboxOptions): UseLightboxReturn {
  const [state, setState] = useState<LightboxState>({
    isOpen: false,
    currentIndex: 0,
    isClosing: false,
    direction: null
  })
  const [lastViewedIndex, setLastViewedIndex] = useState(0)

  const open = useCallback((index: number) => {
    setState({
      isOpen: true,
      currentIndex: Math.max(0, Math.min(index, images.length - 1)),
      isClosing: false,
      direction: null
    })
  }, [images.length])

  const close = useCallback(() => {
    setState(prev => {
      setLastViewedIndex(prev.currentIndex)
      return { ...prev, isClosing: true }
    })
    // Actual close happens after animation
    setTimeout(() => {
      setState(prev => ({ ...prev, isOpen: false, isClosing: false }))
    }, 250)
  }, [])

  const closeAtIndex = useCallback((index: number) => {
    setLastViewedIndex(index)
    setState(prev => ({ ...prev, isClosing: true }))
    // Actual close happens after animation
    setTimeout(() => {
      setState(prev => ({ ...prev, isOpen: false, isClosing: false }))
    }, 250)
  }, [])

  const next = useCallback(() => {
    setState(prev => {
      if (!prev.isOpen) return prev

      const nextIndex = prev.currentIndex + 1
      if (nextIndex >= images.length) {
        return loop
          ? { ...prev, currentIndex: 0, direction: 'right' as const }
          : prev
      }
      return { ...prev, currentIndex: nextIndex, direction: 'right' as const }
    })
  }, [images.length, loop])

  const prev = useCallback(() => {
    setState(prev => {
      if (!prev.isOpen) return prev

      const prevIndex = prev.currentIndex - 1
      if (prevIndex < 0) {
        return loop
          ? { ...prev, currentIndex: images.length - 1, direction: 'left' as const }
          : prev
      }
      return { ...prev, currentIndex: prevIndex, direction: 'left' as const }
    })
  }, [images.length, loop])

  const goTo = useCallback((index: number) => {
    if (index >= 0 && index < images.length) {
      setState(prev => ({
        ...prev,
        currentIndex: index,
        direction: index > prev.currentIndex ? 'right' : 'left'
      }))
    }
  }, [images.length])

  const currentImage = useMemo(() =>
    state.isOpen ? images[state.currentIndex] || null : null,
    [images, state.currentIndex, state.isOpen]
  )

  return {
    state,
    open,
    close,
    closeAtIndex,
    next,
    prev,
    goTo,
    currentImage,
    totalImages: images.length,
    lastViewedIndex
  }
}
